import { PREFERENCE, readPreference, writePreference } from './prefs'
import { useCallback, useEffect, useState } from 'react'

export type ThemeChoice = 'system' | 'light' | 'dark'

function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
}

/** The theme actually shown: a 'system' choice resolved against the system. */
export function resolvedTheme(choice: ThemeChoice): 'light' | 'dark' {
  return choice === 'dark' || (choice === 'system' && systemPrefersDark()) ? 'dark' : 'light'
}

function apply(choice: ThemeChoice): void {
  document.documentElement.dataset.theme = resolvedTheme(choice)
}

/** Follows the system by default; an explicit choice is remembered. */
export function useTheme(): [ThemeChoice, (next: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(() => {
    const stored = readPreference(PREFERENCE.theme)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  })

  useEffect(() => {
    apply(choice)
    if (choice !== 'system') return
    // Keep tracking the system while no explicit choice is in force.
    const media = matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => apply('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [choice])

  const set = useCallback((next: ThemeChoice) => {
    setChoice(next)
    writePreference(PREFERENCE.theme, next)
  }, [])

  return [choice, set]
}
